/**
 * FolderBreadcrumb — clickable path segments for the FolderPicker.
 *
 * Splits a `currentPrefix` like `clients/mini-zali/` into segments and
 * renders each as a clickable link. Clicking a segment navigates back up
 * the tree (the picker re-fires `listFolders` with the truncated prefix).
 *
 * Canonical form: paths carry a trailing slash everywhere
 * (`clients/mini-zali/`). The component normalizes its input by stripping
 * a trailing slash before splitting, so callers can pass either form.
 *
 * Root segment label: defaults to "Bucket root" but can be overridden via
 * `rootLabel` (e.g. the bucket name from the listFolders response).
 */

import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FolderBreadcrumbProps {
  /** Current prefix (with or without trailing slash). */
  currentPrefix: string;
  /** Label for the root segment (e.g. bucket name). */
  rootLabel?: string;
  /** Called when a segment (or root) is clicked, with the new prefix. */
  onNavigate: (newPrefix: string) => void;
  /** Disable navigation while parent is mutating (e.g. saving prefix). */
  disabled?: boolean;
}

export function FolderBreadcrumb({
  currentPrefix,
  rootLabel = 'Bucket root',
  onNavigate,
  disabled = false,
}: FolderBreadcrumbProps) {
  // Normalize: split filters out empties, so trailing/leading slashes are
  // handled transparently.
  const segments = currentPrefix.split('/').filter(Boolean);

  return (
    <nav aria-label="Folder path" className="flex items-center gap-1 flex-wrap">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs font-medium"
        onClick={() => onNavigate('')}
        disabled={disabled}
      >
        {rootLabel}
      </Button>
      {segments.map((segment, idx) => {
        // Reconstruct the prefix up to and including this segment, with
        // trailing slash (canonical form).
        const prefixUpToHere = `${segments.slice(0, idx + 1).join('/')}/`;
        const isLast = idx === segments.length - 1;
        return (
          <span key={prefixUpToHere} className="flex items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/70" aria-hidden="true" />
            <Button
              type="button"
              variant={isLast ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2 text-xs font-mono"
              onClick={() => onNavigate(prefixUpToHere)}
              disabled={disabled || isLast}
              aria-current={isLast ? 'page' : undefined}
            >
              {segment}
            </Button>
          </span>
        );
      })}
    </nav>
  );
}
